<script setup lang="ts" generic="T extends Record<string, unknown> = Record<string, unknown>">
import { computed, useSlots } from 'vue';
import { ArrowUpDown } from 'lucide-vue-next';
import { Card, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import DataTableEmpty from './DataTableEmpty.vue';
import { useSortableHeader } from './useSortableHeader';
import type { RowAction, SortConfig, TableColumn } from './types';

const props = withDefaults(
  defineProps<{
    columns?: TableColumn[];
    rows?: T[];
    sort?: SortConfig;
    card?: boolean;
    title?: string;
    summary?: string;
    isEmpty?: boolean;
    emptyTitle?: string;
    emptyBody?: string;
    clickFn?: (row: T) => void;
    linkFn?: (row: T) => string;
    stickyFirstColumn?: boolean;
    rowActions?: RowAction<T>[];
  }>(),
  { card: true, columns: () => [], rows: () => [], stickyFirstColumn: false, rowActions: () => [] }
);

const emit = defineEmits<{ sort: [sort: SortConfig] }>();

const slots = useSlots();

const currentSort = computed<SortConfig>(() => props.sort ?? { key: '', order: 'desc' });
const { nextSort, sortIconClass } = useSortableHeader(() => currentSort.value);

const hasCustomBody = computed(() => !!slots.body);
const empty = computed(() => props.isEmpty ?? props.rows.length === 0);
const hasHeader = computed(() => !!props.title || !!props.summary || !!slots.header);
const showEmpty = computed(() => empty.value && (!!slots.empty || !!props.emptyTitle));

const clickable = computed(() => typeof props.clickFn === 'function');
const linkable = computed(() => typeof props.linkFn === 'function');
const hasRowActions = computed(() => props.rowActions.length > 0);

const handleRowClick = (row: T) => {
  if (clickable.value && props.clickFn) props.clickFn(row);
};

const formatCell = (row: T, column: TableColumn) => {
  const value = row[column.key];
  return column.formatter ? column.formatter(value) : value;
};
</script>

<template>
  <component :is="card ? Card : 'div'" :class="card ? 'mb-0 gap-0 py-3' : ''">
    <div v-if="hasHeader" class="border-b border-gray-200 px-4 pb-3">
      <slot name="header">
        <div class="flex flex-col gap-3">
          <div v-if="title || summary" class="space-y-1">
            <CardTitle v-if="title" class="text-base">{{ title }}</CardTitle>
            <p v-if="summary" class="text-xs text-slate-500">{{ summary }}</p>
          </div>
          <slot name="toolbar" />
        </div>
      </slot>
    </div>

    <slot name="prebody" />

    <template v-if="showEmpty">
      <slot name="empty">
        <DataTableEmpty :title="emptyTitle ?? ''" :body="emptyBody" />
      </slot>
    </template>

    <slot v-else-if="hasCustomBody" name="body" />

    <Table v-else>
      <TableHeader>
        <TableRow>
          <TableHead v-for="column in columns" :key="`th-${column.key}`" :class="column.thClass">
            <button
              v-if="column.sortable !== false"
              class="flex items-center gap-1 text-left transition-colors hover:text-gray-900"
              @click="emit('sort', nextSort(column.key))"
            >
              {{ column.label }}
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass(column.key)" />
            </button>
            <span v-else>{{ column.label }}</span>
          </TableHead>
          <TableHead v-if="hasRowActions" class="w-16">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow
          v-for="(row, index) in rows"
          :key="`row-${index}`"
          class="group relative"
          :class="{ 'cursor-pointer': clickable || linkable }"
          @click="handleRowClick(row)"
        >
          <TableCell
            v-for="(column, colIndex) in columns"
            :key="`row-${index}-${column.key}`"
            :class="[column.tdClass, colIndex === 0 && stickyFirstColumn ? 'sticky left-0 z-10 bg-white' : '']"
          >
            <a
              v-if="linkable && colIndex === 0"
              :href="linkFn!(row)"
              class="absolute inset-0"
              @click.prevent="handleRowClick(row)"
            >
              <span class="sr-only">View</span>
            </a>
            <slot :name="`cell-${column.key}`" :row="row" :column="column">
              {{ formatCell(row, column) }}
            </slot>
          </TableCell>
          <TableCell v-if="hasRowActions" class="flex space-x-2" @click.stop>
            <button
              v-for="(action, actionIndex) in rowActions"
              :key="`row-${index}-action-${actionIndex}`"
              :title="action.tooltip"
              :class="action.class"
              class="rounded-md p-1 hover:bg-gray-100"
              @click="action.action(row)"
            >
              <vue-feather :type="action.icon" class="h-4 w-4" />
            </button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>

    <slot name="footer" />
  </component>
</template>
